import { useMemo } from 'react';
import { Alert, Badge } from '@playerbase/ui';
import type { DiscordGuildRole } from '@playerbase/types';

// ---------------------------------------------------------------------------
// Inline role multi-select. Mirrors DiscordChannelMultiSelect's shape so
// the Recruit card can render channel + role pickers side by side with
// matching affordances.
//
// Filtering rules:
//   - @everyone (id == guildId, position 0) is hidden. Granting it is
//     a no-op in Discord's model and would be confusing.
//   - Managed roles (integration / bot owned) are hidden because the
//     bot can't assign them anyway; surfacing them is a footgun.
//
// Selection is fully controlled by the parent.
// ---------------------------------------------------------------------------

interface DiscordRoleMultiSelectProps {
  roles: DiscordGuildRole[];
  selectedIds: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
  loading: boolean;
  disabled?: boolean;
  emptyMessage?: string;
}

function pickableRoles(roles: DiscordGuildRole[]): DiscordGuildRole[] {
  // @everyone always sits at position 0 with name "@everyone"; the
  // position guard alone would catch a custom role someone renamed to
  // "@everyone" too. Belt and braces.
  return roles.filter((r) => !r.managed && r.position > 0);
}

export function DiscordRoleMultiSelect({
  roles,
  selectedIds,
  onChange,
  loading,
  disabled = false,
  emptyMessage = "The bot can't see any assignable roles in this server yet.",
}: DiscordRoleMultiSelectProps): JSX.Element {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const visibleRoles = useMemo(() => pickableRoles(roles), [roles]);

  // Stale-id guard: a role previously picked may have been deleted (or
  // become managed). Surface separately so the studio can clear it.
  const missingIds = useMemo(() => {
    const known = new Set(visibleRoles.map((r) => r.externalId));
    return selectedIds.filter((id) => !known.has(id));
  }, [visibleRoles, selectedIds]);

  function toggle(roleId: string): void {
    if (selectedSet.has(roleId)) {
      onChange(selectedIds.filter((id) => id !== roleId));
    } else {
      onChange([...selectedIds, roleId]);
    }
  }

  if (loading && roles.length === 0) {
    return <p className="text-sm text-text-muted">Loading roles…</p>;
  }
  if (visibleRoles.length === 0 && missingIds.length === 0) {
    return <Alert tone="info">{emptyMessage}</Alert>;
  }

  return (
    <div className="max-h-72 overflow-y-auto rounded-md border border-border">
      <ul className="divide-y divide-border">
        {visibleRoles.map((r) => {
          const ticked = selectedSet.has(r.externalId);
          return (
            <li key={r.externalId} className="px-3 py-1">
              <label
                className={`flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm ${
                  disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent-bg'
                }`}
              >
                <input
                  type="checkbox"
                  checked={ticked}
                  disabled={disabled}
                  onChange={() => toggle(r.externalId)}
                />
                {r.colorHex !== null ? (
                  <span
                    aria-hidden
                    className="inline-block h-3 w-3 rounded-full border border-border"
                    style={{ backgroundColor: r.colorHex }}
                  />
                ) : (
                  <span aria-hidden className="inline-block h-3 w-3 rounded-full border border-border bg-transparent" />
                )}
                <span className="flex-1 truncate">{r.name}</span>
                {r.hoist ? <Badge tone="neutral">Hoisted</Badge> : null}
              </label>
            </li>
          );
        })}
        {missingIds.length > 0 ? (
          <li className="px-3 py-2">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-danger">
              Missing
            </p>
            <ul className="space-y-1">
              {missingIds.map((id) => (
                <li key={id}>
                  <label
                    className={`flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm ${
                      disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent-bg'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked
                      disabled={disabled}
                      onChange={() => toggle(id)}
                    />
                    <span className="flex-1 truncate text-text-muted">
                      Role no longer assignable ({id})
                    </span>
                    <Badge tone="warning">Stale</Badge>
                  </label>
                </li>
              ))}
            </ul>
          </li>
        ) : null}
      </ul>
    </div>
  );
}
